import { ShieldOff } from 'lucide-react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '@/lib/auth';

function LoadingGate() {
  return (
    <div className="flex min-h-48 items-center justify-center text-sm text-slate-500">
      Checking your session…
    </div>
  );
}

function AccessDenied({ reason }: { reason: string }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
      <ShieldOff className="h-8 w-8 text-slate-400" />
      <div>
        <p className="font-semibold text-slate-900">Access denied</p>
        <p className="mt-1 text-sm text-slate-500">{reason}</p>
      </div>
      <Link to="/" className="text-brand-600 hover:text-brand-700 text-sm font-semibold">
        Back to home
      </Link>
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingGate />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingGate />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!user?.isAdmin) {
    return <AccessDenied reason="This page is only available to Jemmi administrators." />;
  }
  return <>{children}</>;
}

export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingGate />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!user?.isSuperAdmin) {
    return <AccessDenied reason="This page is only available to Jemmi super administrators." />;
  }
  return <>{children}</>;
}
